/**
 * B1-#9: Keyboard shortcuts overlay — shown when user presses "?".
 */
import { useEffect } from 'react'
import { T } from '../pages/shared'
import { SHORTCUTS } from '../hooks/useKeyboardShortcuts'

interface Props {
  onClose: () => void
}

export default function KeyboardShortcuts({ onClose }: Props) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.surface2,
          border: `1px solid ${T.border2}`,
          borderRadius: 14,
          padding: '28px 32px',
          maxWidth: 520,
          width: '100%',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          fontFamily: T.fontBody,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, fontFamily: T.fontDisplay, color: T.textPrimary, letterSpacing: -0.4 }}>
              Keyboard Shortcuts
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>
              Works when no input is focused
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close shortcuts"
            style={{
              width: 28, height: 28, borderRadius: '50%',
              border: `1px solid ${T.border2}`,
              background: T.surface3,
              color: T.textMuted,
              cursor: 'pointer', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
          {SHORTCUTS.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <kbd style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 28, height: 28, padding: '0 7px',
                background: T.surface1,
                border: `1px solid ${T.border2}`,
                borderBottom: `2px solid ${T.border2}`,
                borderRadius: 6,
                fontFamily: T.fontMono,
                fontSize: 12, fontWeight: 700,
                color: T.canyon,
                letterSpacing: 0,
              }}>
                {s.key}
              </kbd>
              <span style={{ fontSize: 13, color: T.textSecondary }}>{s.description}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 22, paddingTop: 14, borderTop: `1px solid ${T.border}`, fontSize: 11, color: T.textMuted, display: 'flex', justifyContent: 'space-between' }}>
          <span>Press <kbd style={{ fontFamily: T.fontMono, color: T.canyon, background: T.surface1, padding: '1px 5px', borderRadius: 4, border: `1px solid ${T.border2}` }}>?</kbd> to open / close</span>
          <span>Press <kbd style={{ fontFamily: T.fontMono, color: T.canyon, background: T.surface1, padding: '1px 5px', borderRadius: 4, border: `1px solid ${T.border2}` }}>Esc</kbd> to dismiss</span>
        </div>
      </div>
    </div>
  )
}
